/* global Excel */
import { setProducts } from "../../slices/productsSlice";
import { clearSheetDiffs, upsertDiffRow } from "../../slices/diffSlice";
import type { MagentoAccount } from "../../slices/accountsSlice";
import store from "../../store/store";
import { fetchProducts, fetchSingleProduct, upsertProduct } from "../magento/adobeCommerce";
import { shallowObjectMatch } from "../common";
import { Product, DiffSyncResult } from "./types";
import { 
  getPageNumberFromName, 
  toComparable, 
  extractProductFromResponse, 
  upsertProductInList 
} from "./utils";
import { 
  applySyncResults, 
  ensureWorksheetForPage, 
  highlightDiffsOnSheet, 
  insertProductsToSheet, 
  BLOCK_COLOR, 
  DEFAULT_PAGE_SIZE 
} from "./dom";

export async function syncDiffsToMagento(
  account: MagentoAccount,
  sheetId: string,
  diffRowsOverride?: Array<{ sheetId: string; rowIndex: number; isOutOfSync?: boolean; data: Record<string, any> }>
) {
  const diffRows = diffRowsOverride ?? Object.values(store.getState().diffs.rows);
  const sheetDiffRows = diffRows.filter((row) => row.sheetId === sheetId);
  if (!sheetDiffRows.length) return { results: [], failedRows: [] as typeof sheetDiffRows };
  if (!account) {
    return { results: [], failedRows: sheetDiffRows };
  }

  const results: DiffSyncResult[] = [];
  const failedRows: typeof sheetDiffRows = [];
  let updatedProducts = [...store.getState().products.items];
  let productsChanged = false;
  const existingSkuSet = new Set(
    updatedProducts.map((product) => toComparable(product?.sku)).filter(Boolean)
  );

  for (const diffRow of sheetDiffRows) {
    if (diffRow?.isOutOfSync === true) {
      failedRows.push(diffRow);
      continue;
    }

    try {
      const initialProductData = store
        .getState()
        .products.items.find((product) => toComparable(product?.sku) === diffRow.data?.sku);

      const apiProductData = await fetchSingleProduct(account, diffRow.data?.sku);
      const isProductUnchangedFromApi = shallowObjectMatch(initialProductData, apiProductData, [
        "sku",
        "price",
        "quantity",
      ]);

      if (!isProductUnchangedFromApi) {
        failedRows.push({
          ...diffRow,
          isOutOfSync: true,
        });

        await Excel.run(async (context) => {
          const sheet = context.workbook.worksheets.getItemOrNullObject(sheetId);
          sheet.load("name");
          await context.sync();
          if (sheet.isNullObject) return;

          const usedRange = sheet.getUsedRangeOrNullObject(true);
          usedRange.load(["rowCount", "columnCount"]);
          await context.sync();
          if (usedRange.isNullObject || usedRange.rowCount === 0) return;

          const rowRange = sheet.getRangeByIndexes(diffRow.rowIndex, 0, 1, usedRange.columnCount);
          rowRange.format.fill.color = BLOCK_COLOR;
          await context.sync();
        });

        continue;
      }
    } catch (error) {
      console.error("Failed to fetch and compare product:", error);
      failedRows.push(diffRow);
    }

    try {
      const rowSku = toComparable(diffRow.data?.sku);
      const method: "POST" | "PUT" = rowSku && existingSkuSet.has(rowSku) ? "PUT" : "POST";
      if (method === "POST" && !rowSku) {
        failedRows.push(diffRow);
        continue;
      }
      const response = await upsertProduct(account, diffRow.data, method);
      const sku = response?.sku || response?.product?.sku || diffRow.data.sku;
      const responseProduct = extractProductFromResponse(response, diffRow.data, sku);

      if (responseProduct) {
        updatedProducts = upsertProductInList(updatedProducts, responseProduct);
        productsChanged = true;
      }

      results.push({ sheetId, rowIndex: diffRow.rowIndex, sku, data: diffRow.data });
    } catch (error) {
      console.error("Failed to upsert product:", error);
      failedRows.push(diffRow);
    }
  }

  if (productsChanged) {
    store.dispatch(setProducts(updatedProducts));
  }

  await applySyncResults(sheetId, results);
  return { results, failedRows };
}

export async function pullConflictsFromMagento(
  account: MagentoAccount,
  sheetId: string,
  conflictRows: Array<{ sheetId: string; rowIndex: number; isOutOfSync?: boolean; data: Record<string, any> }>
) {
  if (!account || !conflictRows.length) return;

  const results: DiffSyncResult[] = [];
  let updatedProducts = [...store.getState().products.items];
  let productsChanged = false;

  for (const diffRow of conflictRows) {
    if (!diffRow.isOutOfSync) continue;

    try {
      const sku = diffRow.data?.sku;
      if (!sku) continue;
      
      const apiProductData = await fetchSingleProduct(account, sku);
      
      if (apiProductData) {
        updatedProducts = upsertProductInList(updatedProducts, apiProductData);
        productsChanged = true;
      }
      
      results.push({ sheetId, rowIndex: diffRow.rowIndex, sku, data: apiProductData });
    } catch (error) {
      console.error(`Failed to pull conflict for SKU ${diffRow.data?.sku}:`, error);
    }
  }

  if (productsChanged) {
    store.dispatch(setProducts(updatedProducts));
  }

  const { applyPullResults } = await import("./dom");
  await applyPullResults(sheetId, results);
}

export async function syncActiveSheetWithMagento(
  account: MagentoAccount,
  pageSize = DEFAULT_PAGE_SIZE
) {
  if (!account) return;

  try {
    let sheetId = "";
    let sheetName = "Page 1";
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getActiveWorksheet();
      sheet.load(["id", "name"]);
      await context.sync();
      sheetId = sheet.id;
      sheetName = sheet.name;
    });

    if (!sheetId) return;

    const page = getPageNumberFromName(sheetName);
    const { items: products } = await fetchProducts(account, pageSize, page);
    store.dispatch(setProducts(products));
    store.dispatch(clearSheetDiffs(sheetId));
    await insertProductsToSheet(sheetId, products);
    await highlightDiffsOnSheet(sheetId, products);
  } catch (error) {
    console.error("Failed to sync active sheet with Magento:", error);
  }
}

export async function syncProductsToWorkbookPages(
  account: MagentoAccount,
  pageSize = 100,
  onProgress?: (fetched: number, total: number) => void
) {
  const resolvedAccount = account;
  if (!resolvedAccount) return [];

  const allProducts: Product[] = [];
  let page = 1;

  const resolvedPageSize = Number(process.env.SYNC_PAGE_SIZE) || pageSize || 100;
  const maxPages = process.env.MAX_SYNC_PAGES ? Number(process.env.MAX_SYNC_PAGES) : Infinity;

  const { items: firstPageItems, totalCount } = await fetchProducts(resolvedAccount, resolvedPageSize, page);
  if (!firstPageItems.length) return [];

  const sheetId = await ensureWorksheetForPage(page);
  await insertProductsToSheet(sheetId, firstPageItems);
  store.dispatch(clearSheetDiffs(sheetId));
  allProducts.push(...firstPageItems);
  onProgress?.(allProducts.length, totalCount);

  if (firstPageItems.length < resolvedPageSize) {
    store.dispatch(setProducts(allProducts));
    return allProducts;
  }

  const totalPagesToFetch = Math.min(Math.ceil(totalCount / resolvedPageSize), maxPages);
  const remainingPages = [];
  for (let i = 2; i <= totalPagesToFetch; i++) {
    remainingPages.push(i);
  }

  const concurrency = 3;
  for (let i = 0; i < remainingPages.length; i += concurrency) {
    const chunk = remainingPages.slice(i, i + concurrency);
    const promises = chunk.map(async (p) => {
      const { items } = await fetchProducts(resolvedAccount, resolvedPageSize, p);
      return { page: p, items };
    });

    const results = await Promise.all(promises);

    for (const result of results) {
      if (!result.items.length) continue;
      const sId = await ensureWorksheetForPage(result.page);
      await insertProductsToSheet(sId, result.items);
      store.dispatch(clearSheetDiffs(sId));
      allProducts.push(...result.items);
      onProgress?.(allProducts.length, totalCount);
    }
  }

  store.dispatch(setProducts(allProducts));
  return allProducts;
}
